import { Navigate, createBrowserRouter, useLocation, useParams } from 'react-router-dom';
import { CircularProgress, Stack } from '@mui/material';
import {
  ManageSsccs,
  ReceiptPurchaseOrders,
  ReceiveManifest,
  ReceiveManifestV2,
  ReceiveManifestDetails,
  ReceiveManifestDetailsV2,
  SSCCDetails,
  SearchReprint,
  Settings,
  AdminPanel,
  QmCoordinatorDashboard
} from './pages';
import { Layout } from './components';
import { useQmFeatureFlags } from './hooks';
import useObserveTripsheet from './hooks/useObserveTripsheet';
import EditPrinters from './pages/admin/printers';
import AdminPanelAccessSettings from './pages/admin/adminPanelAccessSettings';
import WarehouseEmailConfig from './pages/settings/WarehouseEmailConfig';

import ManageLocations from './pages/admin/locations/ManageLocations';
import ManageSubLocations from './pages/admin/locations/ManageSubLocations';
import { useUserDetails } from './contexts/UserDetailsContext';
import { ManifestPackageStatus, ManifestStatus } from './common/status';

function QmCoordinatorRoute() {
  const { isLoading } = useUserDetails();

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '40vh' }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return <QmCoordinatorDashboard />;
}

function ReceiveManifestListRoute() {
  const { qmSiteInboundEnabled } = useQmFeatureFlags();

  return qmSiteInboundEnabled ? <ReceiveManifestV2 /> : <ReceiveManifest />;
}

function ReceiveManifestDetailRoute() {
  const { qmSiteInboundEnabled } = useQmFeatureFlags();
  const { id } = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const manifestUiVersion = searchParams.get('manifestUiVersion');
  const stateTripId = String((location.state as { tripId?: string } | undefined)?.tripId || '').trim();
  const tripId = String(searchParams.get('tripId') || stateTripId || '').trim();

  // Keep hook order stable: always call the hook, and gate decisions below by feature flag/version hint.
  const tripsheet = useObserveTripsheet(tripId);

  const manifest =
    id && tripsheet
      ? tripsheet.legs.flatMap((leg) => leg.manifests).find((candidate) => candidate.id === id)
      : undefined;

  const shouldUseV1ForOpenManifest =
    manifest?.status === ManifestStatus.open &&
    Boolean(
      manifest?.ssccs.some((sscc) =>
        sscc.packages.some(
          (pkg) =>
            pkg.receivedStatus === ManifestPackageStatus.received ||
            pkg.receivedStatus === ManifestPackageStatus.partial ||
            pkg.receivedStatus === ManifestPackageStatus.qm
        )
      )
    );

  if (!qmSiteInboundEnabled) {
    return <ReceiveManifestDetails />;
  }

  if (manifestUiVersion === 'v1') {
    return <ReceiveManifestDetails />;
  }

  if (manifestUiVersion === 'v2') {
    return <ReceiveManifestDetailsV2 />;
  }

  return shouldUseV1ForOpenManifest ? <ReceiveManifestDetails /> : <ReceiveManifestDetailsV2 />;
}

export const router = createBrowserRouter([
  {
    path: '/manage-ssccs',
    element: (
      <Layout>
        <ManageSsccs />
      </Layout>
    )
  },
  {
    path: '/manage-ssccs/:id',
    element: (
      <Layout>
        <SSCCDetails />
      </Layout>
    )
  },
  {
    path: '/receipt-purchase-orders',
    element: (
      <Layout>
        <ReceiptPurchaseOrders />
      </Layout>
    )
  },
  {
    path: '/receipt-purchase-orders/:id',
    element: (
      <Layout>
        <ReceiptPurchaseOrders />
      </Layout>
    )
  },
  {
    path: '/receive-manifest',
    element: (
      <Layout>
        <ReceiveManifestListRoute />
      </Layout>
    )
  },
  {
    path: '/receive-manifest/:id',
    element: (
      <Layout>
        <ReceiveManifestDetailRoute />
      </Layout>
    )
  },
  {
    path: '/search-reprint',
    element: (
      <Layout>
        <SearchReprint />
      </Layout>
    )
  },
  {
    path: '/admin-panel',
    element: (
      <Layout>
        <AdminPanel />
      </Layout>
    )
  },
  {
    path: '/settings',
    element: (
      <Layout>
        <Settings />
      </Layout>
    )
  },
  {
    path: '/settings/locations',
    element: (
      <Layout>
        <ManageLocations />
      </Layout>
    )
  },
  {
    path: '/settings/location/:id',
    element: (
      <Layout>
        <ManageSubLocations />
      </Layout>
    )
  },
  {
    path: '/settings/printers',
    element: (
      <Layout>
        <EditPrinters />
      </Layout>
    )
  },
  {
    path: '/settings/warehouse-emails',
    element: (
      <Layout>
        <WarehouseEmailConfig />
      </Layout>
    )
  },
  {
    path: '/settings/adminPanelAccessSettings',
    element: (
      <Layout>
        <AdminPanelAccessSettings />
      </Layout>
    )
  },
  {
    path: '/qm-cases',
    element: (
      <Layout>
        <QmCoordinatorRoute />
      </Layout>
    )
  },
  {
    path: '/qm-coordinator',
    element: <Navigate to="/qm-cases" replace />
  },
  {
    path: '/',
    element: (
      <Layout>
        <ReceiptPurchaseOrders />
      </Layout>
    )
  }
]);
